import { BrandOverlayEntityRaw, BrandOverlayIsInUse } from "@common/domain";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";
import { createReducer, on, Action } from "@ngrx/store";
import { flatMap } from "lodash";
import { BrandOverlayActions, BrandActions } from "../actions";

export const BRAND_OVERLAY_FEATURE_KEY = "brandOverlay";

export interface BrandOverlayState extends EntityState<BrandOverlayEntityRaw> {}

export const brandOverlayAdapter: EntityAdapter<BrandOverlayEntityRaw> =
  createEntityAdapter<BrandOverlayEntityRaw>({
    selectId: (_) => _.id,
    sortComparer: (lhs, rhs) => {
      if (lhs.isDefault && rhs.isDefault) {
        return lhs.id - rhs.id;
      }

      if (lhs.isDefault) {
        return -1;
      }

      if (rhs.isDefault) {
        return 1;
      }

      return lhs.id - rhs.id;
    }
  });

export const initialBrandOverlayState: BrandOverlayState =
  brandOverlayAdapter.getInitialState({});

const reducer = createReducer(
  initialBrandOverlayState,
  on(BrandActions.brandGetListSuccess, (state, { brands }) =>
    brandOverlayAdapter.addMany(
      flatMap(brands, (_) => _.value.overlays),
      state
    )
  ),
  on(BrandActions.brandCreateSuccess, (state, { brand }) => {
    return brandOverlayAdapter.addMany(brand.value.overlays, state);
  }),
  on(BrandActions.brandDeleteSuccess, (state, { brandId }) => {
    const overlays = Object.values(state.entities).filter(
      (_) => _?.brandId === brandId
    );

    return brandOverlayAdapter.removeMany(
      overlays.map((_) => _!.id),
      state
    );
  }),
  on(BrandOverlayActions.brandOverlayCreateSuccess, (state, { brandOverlay }) =>
    brandOverlayAdapter.addOne(brandOverlay.toJSON(), state)
  ),
  on(BrandOverlayActions.brandOverlayDeleteSuccess, (state, { brandOverlay }) =>
    brandOverlayAdapter.removeOne(brandOverlay.value.id, state)
  ),
  on(BrandOverlayActions.brandOverlayUnUseSuccess, (state, { brandOverlay }) =>
    brandOverlayAdapter.updateOne(
      {
        id: brandOverlay.value.id,
        changes: { isInUse: false as BrandOverlayIsInUse }
      },
      state
    )
  ),
  on(BrandOverlayActions.brandOverlayUseSuccess, (state, { brandOverlay }) => {
    const overlays = Object.values(state.entities).filter(
      (_) => _?.brandId === brandOverlay.value.brandId
    );

    return brandOverlayAdapter.updateOne(
      {
        id: brandOverlay.value.id,
        changes: { isInUse: true as BrandOverlayIsInUse }
      },
      brandOverlayAdapter.updateMany(
        overlays.map((_) => ({
          id: _!.id,
          changes: { isInUse: false as BrandOverlayIsInUse }
        })),
        state
      )
    );
  })
);

export function brandOverlayReducer(
  state: BrandOverlayState | undefined,
  action: Action
) {
  return reducer(state, action);
}
